import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CalendarDays } from 'lucide-react'
import {
  ComingSoonAmbience,
  BrandValues,
  HeroCard,
  HeroLogo,
  OrnateDivider,
  useParallax,
} from '@/components/comingSoon/ComingSoonAmbience'
import {
  ComingSoonSocial,
  LaunchLiveReveal,
  LaunchProgressBar,
  NotesTicker,
  RotatingHighlight,
} from '@/components/comingSoon/ComingSoonExtras'
import { COMING_SOON_CONTENT } from '@/lib/comingSoon'
import { useStoreGate } from '@/contexts/StoreGateContext'
import { format } from 'date-fns'

interface Countdown {
  days: number
  hours: number
  minutes: number
  seconds: number
  launched: boolean
}

function getCountdown(target: Date): Countdown {
  const diff = target.getTime() - Date.now()
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, launched: true }
  }
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    launched: false,
  }
}

const countdownUnits = [
  { key: 'days' as const, label: 'Days' },
  { key: 'hours' as const, label: 'Hours' },
  { key: 'minutes' as const, label: 'Mins' },
  { key: 'seconds' as const, label: 'Secs' },
]

function CountdownDigit({ value, label }: { value: number; label: string }) {
  const display = String(value).padStart(2, '0')

  return (
    <div className="coming-soon-digit flex flex-1 flex-col items-center">
      <div className="coming-soon-digit-box relative w-full overflow-hidden rounded-lg px-1 py-2.5 sm:px-2 sm:py-3.5">
        <AnimatePresence mode="popLayout">
          <motion.span
            key={display}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
            className="block font-display text-2xl font-semibold tabular-nums text-gold-100 sm:text-4xl"
          >
            {display}
          </motion.span>
        </AnimatePresence>
      </div>
      <span className="mt-1.5 text-[9px] uppercase tracking-[0.18em] text-zinc-500 sm:text-[10px]">
        {label}
      </span>
    </div>
  )
}

function CountdownSeparator() {
  return (
    <span
      className="mb-5 self-center pt-1 font-display text-lg text-gold-500/35 sm:mb-6 sm:pt-2 sm:text-2xl"
      aria-hidden
    >
      :
    </span>
  )
}

function ComingSoonPreLaunch({ launchLabel }: { launchLabel: string }) {
  const { schedule } = useStoreGate()
  const [countdown, setCountdown] = useState<Countdown>(() =>
    getCountdown(schedule.launchDate)
  )

  useEffect(() => {
    const tick = () => setCountdown(getCountdown(schedule.launchDate))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [schedule.launchDate])

  return (
    <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <HeroCard>
        <HeroLogo />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="coming-soon-badge mb-2 inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] uppercase tracking-[0.35em] text-gold-300/90 sm:text-xs"
        >
          Launching Soon
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="coming-soon-title font-display text-4xl font-bold leading-tight sm:text-5xl md:text-6xl"
        >
          Bazm-e-Musk
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-2.5 font-display text-base italic text-gold-200/70 sm:text-lg"
        >
          {COMING_SOON_CONTENT.tagline}
        </motion.p>

        <RotatingHighlight />

        <OrnateDivider />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="mx-auto max-w-md text-sm leading-relaxed text-zinc-400"
        >
          {COMING_SOON_CONTENT.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-7"
        >
          <div className="coming-soon-countdown-panel rounded-xl px-3 py-5 sm:px-5 sm:py-6">
            <div className="mb-4 flex justify-center">
              <span className="inline-flex items-center gap-1.5 text-xs text-gold-300/80">
                <CalendarDays className="h-3.5 w-3.5 text-gold-400/80" />
                Launching {launchLabel}
              </span>
            </div>

            <div className="flex items-center justify-center gap-0.5 sm:gap-1">
              {countdownUnits.map(({ key, label }, i) => (
                <div key={key} className="contents">
                  <CountdownDigit value={countdown[key]} label={label} />
                  {i < countdownUnits.length - 1 && <CountdownSeparator />}
                </div>
              ))}
            </div>

            <LaunchProgressBar />
          </div>
        </motion.div>

        <BrandValues />
      </HeroCard>

      <ComingSoonSocial />
      <NotesTicker />

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="mt-5 text-center text-[10px] uppercase tracking-[0.22em] text-zinc-600 sm:text-xs"
      >
        {COMING_SOON_CONTENT.footerTagline}
      </motion.p>
    </div>
  )
}

export function ComingSoonPage() {
  const { schedule, storeLaunched } = useStoreGate()
  const { x, y, xSlow, ySlow, onMouseMove } = useParallax()
  const showLiveRevealRef = useRef(storeLaunched)
  if (storeLaunched) showLiveRevealRef.current = true
  const showLiveReveal = showLiveRevealRef.current

  const launchLabel = format(schedule.launchDate, 'd MMMM yyyy · h:mm a')

  return (
    <div
      className={`coming-soon-page relative min-h-screen overflow-x-hidden${showLiveReveal ? ' coming-soon-page--live' : ''}`}
      onMouseMove={onMouseMove}
    >
      <ComingSoonAmbience x={x} y={y} xSlow={xSlow} ySlow={ySlow} />
      <div className="coming-soon-shimmer" aria-hidden />

      <AnimatePresence mode="wait">
        {showLiveReveal ? (
          <LaunchLiveReveal key="live" />
        ) : (
          <motion.div
            key="prelaunch"
            initial={{ opacity: 1 }}
            exit={{
              opacity: 0,
              scale: 0.94,
              filter: 'blur(12px)',
            }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] as const }}
          >
            <ComingSoonPreLaunch launchLabel={launchLabel} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
